/* global AbortController */
import { Controller } from '@hotwired/stimulus'

const POSTHOG_EVENT_PROMPT_DISPLAYED = 'pilot:prompts:displayed'
const POSTHOG_EVENT_PROMPT_CLICKED = 'pilot:prompts:clicked'
const POSTHOG_EVENT_PROMPT_REFRESHED = 'pilot:prompts:refreshed'

const REQUEST_TIMEOUT = 10000 // 10 seconds
const MAX_RETRY_ATTEMPTS = 2

export default class extends Controller {
  get agentKey() {
    return (
      this.element.dataset.pilotPromptSuggestionsAgentKeyValue ||
      this.element.getAttribute('data-pilot--prompt-suggestions-agent-key-value') ||
      'solo'
    )
  }

  get pilotThreadId() {
    return (
      this.element.dataset.pilotPromptSuggestionsPilotThreadIdValue ||
      this.element.getAttribute('data-pilot--prompt-suggestions-pilot-thread-id-value')
    )
  }

  get threadKey() {
    return (
      this.element.dataset.pilotPromptSuggestionsThreadKeyValue ||
      this.element.getAttribute('data-pilot--prompt-suggestions-thread-key-value')
    )
  }

  get userContext() {
    return (
      this.element.dataset.pilotPromptSuggestionsUserContextValue ||
      this.element.getAttribute('data-pilot--prompt-suggestions-user-context-value')
    )
  }

  get endpoint() {
    return (
      this.element.dataset.pilotPromptSuggestionsEndpointValue ||
      this.element.getAttribute('data-pilot--prompt-suggestions-endpoint-value') ||
      '/pilot/completions/prompt_suggestions'
    )
  }

  connect() {
    this.suggestions = []
    this.isLoading = false
    this.hasDisplayedSuggestions = false
    this.retryCount = 0
    this.abortController = null

    this.fetchSuggestions()

    this.boundHandleStreamingFinished = this.handleStreamingFinished.bind(this)
    this.boundHandlePillClick = this.handlePillClick.bind(this)

    document.addEventListener('pilot:streaming:finished', this.boundHandleStreamingFinished)
    this.element.addEventListener('pill-click', this.boundHandlePillClick)
  }

  disconnect() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }

    document.removeEventListener('pilot:streaming:finished', this.boundHandleStreamingFinished)
    this.element.removeEventListener('pill-click', this.boundHandlePillClick)
  }

  async fetchSuggestions() {
    if (this.isLoading) {
      return
    }

    this.isLoading = true
    this.updateLoadingState(true)

    if (this.abortController) {
      this.abortController.abort()
    }

    this.abortController = new AbortController()

    try {
      const params = new URLSearchParams({
        agent_key: this.agentKey,
      })

      if (this.pilotThreadId) params.append('pilot_thread_id', this.pilotThreadId)
      if (this.threadKey) params.append('thread_key', this.threadKey)
      if (this.userContext) params.append('user_context', this.userContext)

      const finalUrl = this.endpoint + '?' + params.toString()

      const timeoutId = setTimeout(() => {
        this.abortController.abort()
      }, REQUEST_TIMEOUT)

      const response = await fetch(finalUrl, {
        signal: this.abortController.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        throw new Error('HTTP ' + response.status + ': ' + response.statusText)
      }

      const data = await response.json()
      const suggestions = data.suggestions || []

      if (suggestions.length === 0) {
        this.isLoading = false
        this.updateLoadingState(false)

        this.suggestions = []
        this.updateSuggestions()
      } else {
        this.suggestions = suggestions
        this.retryCount = 0

        this.isLoading = false
        this.updateLoadingState(false)

        this.updateSuggestions()
        this.trackSuggestionsDisplayed()
      }
    } catch (error) {
      this.handleFetchError(error)
    } finally {
      this.isLoading = false
      this.updateLoadingState(false)
      this.abortController = null
    }
  }

  refreshSuggestions() {
    this.retryCount = 0
    this.trackSuggestionsRefreshed()

    const refreshStartEvent = new CustomEvent('prompt-suggestions:refresh-start', {
      detail: { loading: true },
    })
    this.element.dispatchEvent(refreshStartEvent)

    this.fetchSuggestions()
  }

  handlePillClick(event) {
    if (this.isLoading) {
      return
    }

    if (!event.detail) {
      return
    }

    const suggestionId = event.detail.id
    const suggestionText = event.detail.text
    const suggestionContext = event.detail.context

    if (!suggestionId || !suggestionText) {
      return
    }

    this.trackPillClick({
      suggestion_id: suggestionId,
      suggestion_text: suggestionText,
      context: suggestionContext,
      agent_key: this.agentKey,
      thread_id: this.pilotThreadId,
    })
  }

  handleStreamingFinished() {
    this.refreshSuggestions()
  }

  updateSuggestions() {
    const eventDetail = {
      suggestions: this.suggestions,
      loading: this.isLoading,
      timestamp: Date.now(),
    }

    const event = new CustomEvent('prompt-suggestions:updated', {
      detail: eventDetail,
    })

    this.element.dispatchEvent(event)
  }

  updateLoadingState(loading) {
    const event = new CustomEvent('prompt-suggestions:loading', {
      detail: { loading: loading },
    })

    this.element.dispatchEvent(event)
  }

  handleFetchError(error) {
    if (error.name === 'AbortError') {
      return
    }

    if (this.retryCount < MAX_RETRY_ATTEMPTS) {
      this.retryCount++

      setTimeout(() => {
        this.fetchSuggestions()
      }, 1000 * this.retryCount)

      return
    }

    this.suggestions = []
    this.isLoading = false
    this.updateLoadingState(false)
    this.updateSuggestions()

    const event = new CustomEvent('prompt-suggestions:error', {
      detail: { error: error.message, usedFallback: false },
    })

    this.element.dispatchEvent(event)
  }

  trackSuggestionsDisplayed() {
    if (!this.hasDisplayedSuggestions && this.suggestions.length > 0) {
      this.hasDisplayedSuggestions = true

      if (window.posthog) {
        window.posthog.capture(POSTHOG_EVENT_PROMPT_DISPLAYED, {
          suggestion_count: this.suggestions.length,
          agent_key: this.agentKey,
          thread_id: this.pilotThreadId,
          suggestions: this.suggestions.map(function (s) {
            return {
              id: s.id,
              text: s.text,
              source: s.metadata && s.metadata.source,
            }
          }),
        })
      }
    }
  }

  trackPillClick(data) {
    if (window.posthog) {
      window.posthog.capture(POSTHOG_EVENT_PROMPT_CLICKED, data)
    }
  }

  trackSuggestionsRefreshed() {
    if (window.posthog) {
      window.posthog.capture(POSTHOG_EVENT_PROMPT_REFRESHED, {
        agent_key: this.agentKey,
        thread_id: this.pilotThreadId,
        previous_suggestion_count: this.suggestions.length,
      })
    }
  }
}
